import { newIdempotencyKey } from "@station/api-client";
import { LOAN_INSTALLMENT_STATUS_LABELS, type Loan } from "@station/contracts";
import {
  Alert,
  Button,
  Card,
  Checkbox,
  DateInput,
  Dialog,
  EmptyState,
  FormField,
  Input,
  MoneyInput,
  MoneyText,
  Select,
  SkeletonRows,
  StatusBadge,
  Textarea,
  useToast,
} from "@station/ui";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { PiggyBank, Plus, Trash2, Upload } from "lucide-react";
import { type ReactElement, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { companySearchShape } from "../../../features/accounting/company.js";
import { todayIstanbul } from "../../../features/accounting/shared.js";
import {
  SimpleTable,
  tdClass,
  tdRightClass,
  thClass,
  thRightClass,
} from "../../../features/counterparties/shared.js";
import { uploadFile } from "../../../features/station-import.js";
import { useBankAccounts } from "../../../features/treasury/definitions.js";
import { TreasuryPage, useRefreshTreasury } from "../../../features/treasury/shared.js";
import {
  type InstallmentState,
  installmentsTotal,
  installmentTotal,
  minorOf,
} from "../../../features/treasury/treasury-values.js";
import { api } from "../../../lib/api.js";
import { errorMessage } from "../../../lib/errors.js";
import { formatDate } from "../../../lib/format.js";
import { queryKeys } from "../../../lib/query.js";
import { usePermissions } from "../../../lib/session.js";

export const Route = createFileRoute("/_app/finans/krediler")({
  validateSearch: (search) => z.object(companySearchShape).parse(search),
  component: LoansPage,
});

function LoansPage() {
  const { t } = useTranslation();
  const permissions = usePermissions();
  const [creating, setCreating] = useState(false);
  return (
    <TreasuryPage
      title={t("menu.loans")}
      description={t("treasury.loans.description")}
      actions={
        permissions.has("treasury.loan.manage") ? (
          <Button onClick={() => setCreating(true)}>
            <Plus aria-hidden="true" />
            {t("treasury.loans.create")}
          </Button>
        ) : null
      }
    >
      {({ companyId }) => <Loans companyId={companyId} creating={creating} onCreatingChange={setCreating} />}
    </TreasuryPage>
  );
}

function Loans({
  companyId,
  creating,
  onCreatingChange,
}: {
  companyId: string;
  creating: boolean;
  onCreatingChange: (open: boolean) => void;
}): ReactElement {
  const { t } = useTranslation();
  const [openLoanId, setOpenLoanId] = useState<string | null>(null);
  const loans = useQuery({
    queryKey: queryKeys.treasury(companyId, "loans"),
    queryFn: async () => (await api.GET("/loans", { params: { query: { companyId } } })).data!.items,
  });

  return (
    <div className="grid gap-4">
      {loans.error ? (
        <Alert tone="danger">{errorMessage(loans.error)}</Alert>
      ) : loans.isPending ? (
        <SkeletonRows rows={4} />
      ) : loans.data.length === 0 ? (
        <EmptyState
          headingLevel={2}
          icon={<PiggyBank />}
          title={t("treasury.loans.emptyTitle")}
          description={t("treasury.loans.emptyDescription")}
        />
      ) : (
        <ul className="grid gap-3">
          {loans.data.map((loan) => (
            <li key={loan.id}>
              <LoanCard
                companyId={companyId}
                loan={loan}
                expanded={openLoanId === loan.id}
                onToggle={() => setOpenLoanId(openLoanId === loan.id ? null : loan.id)}
              />
            </li>
          ))}
        </ul>
      )}
      <LoanDialog companyId={companyId} open={creating} onClose={() => onCreatingChange(false)} />
    </div>
  );
}

function LoanCard({
  companyId,
  loan,
  expanded,
  onToggle,
}: {
  companyId: string;
  loan: Loan;
  expanded: boolean;
  onToggle: () => void;
}): ReactElement {
  const { t } = useTranslation();
  const toast = useToast();
  const refresh = useRefreshTreasury();
  const canManage = usePermissions().has("treasury.loan.manage");
  const fileRef = useRef<HTMLInputElement>(null);
  const [payingId, setPayingId] = useState<string | null>(null);
  const [paidOn, setPaidOn] = useState<string | null>(todayIstanbul());
  const [editingPlan, setEditingPlan] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  return (
    <Card className="grid gap-3 p-4">
      <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
        <div className="grid gap-0.5">
          <span className="flex flex-wrap items-center gap-2 font-medium">
            {loan.name}
            <StatusBadge tone={loan.status === "ACTIVE" ? "success" : "neutral"}>
              {t(`treasury.loans.status.${loan.status}`)}
            </StatusBadge>
          </span>
          <span className="text-xs text-muted-foreground">
            {loan.bankName} · {loan.bankAccountName}
            {loan.referenceNo ? ` · ${loan.referenceNo}` : ""} ·{" "}
            {t("treasury.loans.startDate", { date: formatDate(loan.startDate) })}
            {loan.annualRate ? ` · %${loan.annualRate}` : ""}
          </span>
        </div>
        <Button variant="ghost" size="sm" onClick={onToggle} aria-expanded={expanded}>
          {expanded ? t("treasury.loans.hidePlan") : t("treasury.loans.showPlan")}
        </Button>
      </div>

      <dl className="flex flex-wrap gap-x-6 gap-y-1 text-sm">
        <div className="flex items-baseline gap-2">
          <dt className="text-muted-foreground">{t("treasury.loans.principal")}</dt>
          <dd className="font-medium">
            <MoneyText value={loan.principal} />
          </dd>
        </div>
        <div className="flex items-baseline gap-2">
          <dt className="text-muted-foreground">{t("treasury.loans.remainingPrincipal")}</dt>
          <dd className="font-medium">
            <MoneyText value={loan.remainingPrincipal} />
          </dd>
        </div>
        <div className="flex items-baseline gap-2">
          <dt className="text-muted-foreground">{t("treasury.loans.nextDue")}</dt>
          <dd className="font-medium">
            {loan.nextDueDate ? (
              <>
                <span className="tabular">{formatDate(loan.nextDueDate)}</span>
                {loan.nextDueAmount ? (
                  <>
                    {" · "}
                    <MoneyText value={loan.nextDueAmount} />
                  </>
                ) : null}
              </>
            ) : (
              "—"
            )}
          </dd>
        </div>
      </dl>

      {error ? <Alert tone="danger">{error}</Alert> : null}

      {expanded ? (
        <>
          <SimpleTable
            caption={t("treasury.loans.installmentsCaption", { name: loan.name })}
            head={
              <tr>
                <th scope="col" className={thRightClass}>
                  {t("treasury.loans.installmentNo")}
                </th>
                <th scope="col" className={thClass}>
                  {t("treasury.dbs.dueDate")}
                </th>
                <th scope="col" className={thRightClass}>
                  {t("treasury.loans.principalAmount")}
                </th>
                <th scope="col" className={thRightClass}>
                  {t("treasury.loans.interestAmount")}
                </th>
                <th scope="col" className={thRightClass}>
                  {t("treasury.loans.otherAmount")}
                </th>
                <th scope="col" className={thRightClass}>
                  {t("common.total")}
                </th>
                <th scope="col" className={thClass}>
                  {t("common.status")}
                </th>
                <th scope="col" className={thClass}>
                  {t("common.actions")}
                </th>
              </tr>
            }
          >
            {loan.installments.map((installment) => (
              <tr key={installment.id} className="border-t">
                <td className={tdRightClass}>{installment.installmentNo}</td>
                <td className={`${tdClass} tabular`}>{formatDate(installment.dueDate)}</td>
                <td className={tdRightClass}>
                  <MoneyText value={installment.principalAmount} />
                </td>
                <td className={tdRightClass}>
                  <MoneyText value={installment.interestAmount} />
                </td>
                <td className={tdRightClass}>
                  <MoneyText value={installment.otherAmount} />
                </td>
                <td className={tdRightClass}>
                  <MoneyText value={installment.totalAmount} className="font-medium" />
                </td>
                <td className={tdClass}>
                  <StatusBadge tone={installment.status === "PAID" ? "success" : "warning"}>
                    {LOAN_INSTALLMENT_STATUS_LABELS[installment.status]}
                  </StatusBadge>
                  {installment.paidOn ? (
                    <>
                      <br />
                      <span className="tabular text-xs text-muted-foreground">
                        {formatDate(installment.paidOn)}
                      </span>
                    </>
                  ) : null}
                </td>
                <td className={tdClass}>
                  {canManage && installment.status === "PLANNED" ? (
                    <Button size="sm" onClick={() => setPayingId(installment.id)}>
                      {t("treasury.loans.pay")}
                    </Button>
                  ) : (
                    <span className="text-muted-foreground">—</span>
                  )}
                </td>
              </tr>
            ))}
          </SimpleTable>

          {canManage ? (
            <div className="flex flex-wrap justify-end gap-2">
              <Button variant="outline" size="sm" loading={busy} onClick={() => fileRef.current?.click()}>
                <Upload aria-hidden="true" />
                {t("treasury.loans.importPlan")}
              </Button>
              <input
                ref={fileRef}
                type="file"
                accept=".csv,.xlsx,.xls"
                className="sr-only"
                tabIndex={-1}
                aria-label={t("treasury.loans.importPlan")}
                onChange={(event) => {
                  const file = event.target.files?.[0];
                  if (!file) return;
                  setBusy(true);
                  setError(null);
                  void uploadFile(file, "IMPORT")
                    .then((fileId) =>
                      api.POST("/loans/{id}/installments/import", {
                        params: { path: { id: loan.id } },
                        body: { fileId, version: loan.version },
                      }),
                    )
                    .then(async () => {
                      await refresh(companyId);
                      toast({ tone: "success", title: t("treasury.loans.planImported") });
                    })
                    .catch((caught: unknown) => setError(errorMessage(caught)))
                    .finally(() => {
                      setBusy(false);
                      if (fileRef.current) fileRef.current.value = "";
                    });
                }}
              />
              <Button variant="outline" size="sm" onClick={() => setEditingPlan(true)}>
                {t("treasury.loans.editPlan")}
              </Button>
            </div>
          ) : null}
        </>
      ) : null}

      <Dialog
        open={payingId !== null}
        onOpenChange={(open) => !open && !busy && setPayingId(null)}
        title={t("treasury.loans.payTitle")}
        description={t("treasury.loans.payDescription")}
        dismissible={!busy}
      >
        <form
          className="grid gap-4"
          noValidate
          onSubmit={(event) => {
            event.preventDefault();
            if (!payingId || !paidOn) return;
            setBusy(true);
            setError(null);
            void api
              .POST("/loan-installments/{id}/pay", {
                params: { path: { id: payingId } },
                body: { paidOn, statementLineId: null },
              })
              .then(async () => {
                await refresh(companyId);
                toast({ tone: "success", title: t("treasury.loans.paid") });
                setPayingId(null);
              })
              .catch((caught: unknown) => setError(errorMessage(caught)))
              .finally(() => setBusy(false));
          }}
        >
          <FormField label={t("treasury.dbs.paidOn")} required>
            {(control) => <DateInput {...control} value={paidOn} onValueChange={setPaidOn} />}
          </FormField>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setPayingId(null)} disabled={busy}>
              {t("common.cancel")}
            </Button>
            <Button type="submit" loading={busy}>
              {t("treasury.loans.pay")}
            </Button>
          </div>
        </form>
      </Dialog>

      <PlanDialog
        companyId={companyId}
        loan={loan}
        open={editingPlan}
        onClose={() => setEditingPlan(false)}
      />
    </Card>
  );
}

function blankInstallment(id: string, installmentNo: number): InstallmentState {
  return {
    id,
    installmentNo,
    dueDate: null,
    principalAmount: null,
    interestAmount: null,
    otherAmount: null,
  };
}

/** Taksit planı satır satır girilir; toplam canlı gösterilir. Excel ile yükleme ayrı düğmededir. */
function InstallmentEditor({
  rows,
  onChange,
}: {
  rows: InstallmentState[];
  onChange: (rows: InstallmentState[]) => void;
}): ReactElement {
  const { t } = useTranslation();
  const patch = (id: string, values: Partial<InstallmentState>) =>
    onChange(rows.map((row) => (row.id === id ? { ...row, ...values } : row)));

  return (
    <div className="grid gap-3">
      <SimpleTable
        caption={t("treasury.loans.planCaption")}
        className="max-h-80 overflow-y-auto"
        head={
          <tr>
            <th scope="col" className={thRightClass}>
              {t("treasury.loans.installmentNo")}
            </th>
            <th scope="col" className={thClass}>
              {t("treasury.dbs.dueDate")}
            </th>
            <th scope="col" className={thRightClass}>
              {t("treasury.loans.principalAmount")}
            </th>
            <th scope="col" className={thRightClass}>
              {t("treasury.loans.interestAmount")}
            </th>
            <th scope="col" className={thRightClass}>
              {t("treasury.loans.otherAmount")}
            </th>
            <th scope="col" className={thRightClass}>
              {t("common.total")}
            </th>
            <th scope="col" className={thClass}>
              <span className="sr-only">{t("common.actions")}</span>
            </th>
          </tr>
        }
        foot={
          <tr>
            <td className={tdClass} colSpan={5}>
              {t("common.total")}
            </td>
            <td className={tdRightClass} aria-live="polite">
              <MoneyText value={installmentsTotal(rows).toString()} />
            </td>
            <td className={tdClass} />
          </tr>
        }
      >
        {rows.map((row, index) => (
          <tr key={row.id} className="border-t">
            <td className={tdRightClass}>{row.installmentNo}</td>
            <td className={tdClass}>
              <DateInput
                className="h-9 w-36"
                aria-label={t("treasury.loans.dueDateFor", { row: index + 1 })}
                value={row.dueDate}
                onValueChange={(dueDate) => patch(row.id, { dueDate })}
              />
            </td>
            <td className={tdClass}>
              <MoneyInput
                className="h-9"
                aria-label={t("treasury.loans.principalFor", { row: index + 1 })}
                value={row.principalAmount}
                onValueChange={(principalAmount) => patch(row.id, { principalAmount })}
              />
            </td>
            <td className={tdClass}>
              <MoneyInput
                className="h-9"
                aria-label={t("treasury.loans.interestFor", { row: index + 1 })}
                value={row.interestAmount}
                onValueChange={(interestAmount) => patch(row.id, { interestAmount })}
              />
            </td>
            <td className={tdClass}>
              <MoneyInput
                className="h-9"
                aria-label={t("treasury.loans.otherFor", { row: index + 1 })}
                value={row.otherAmount}
                onValueChange={(otherAmount) => patch(row.id, { otherAmount })}
              />
            </td>
            <td className={tdRightClass}>
              <MoneyText value={installmentTotal(row).toString()} />
            </td>
            <td className={tdClass}>
              <Button
                variant="ghost"
                size="sm"
                aria-label={t("treasury.loans.removeRow", { row: index + 1 })}
                onClick={() => onChange(rows.filter((item) => item.id !== row.id))}
              >
                <Trash2 aria-hidden="true" />
              </Button>
            </td>
          </tr>
        ))}
      </SimpleTable>
      <Button
        variant="outline"
        size="sm"
        onClick={() =>
          onChange([
            ...rows,
            blankInstallment(`row-${Date.now()}-${rows.length}`, (rows.at(-1)?.installmentNo ?? 0) + 1),
          ])
        }
      >
        <Plus aria-hidden="true" />
        {t("treasury.loans.addInstallment")}
      </Button>
    </div>
  );
}

function toInstallmentPayload(rows: readonly InstallmentState[]) {
  return rows.map((row) => ({
    installmentNo: row.installmentNo,
    dueDate: row.dueDate ?? "",
    principalAmount: minorOf(row.principalAmount).toString(),
    interestAmount: minorOf(row.interestAmount).toString(),
    otherAmount: minorOf(row.otherAmount).toString(),
  }));
}

function LoanDialog({
  companyId,
  open,
  onClose,
}: {
  companyId: string;
  open: boolean;
  onClose: () => void;
}): ReactElement {
  const { t } = useTranslation();
  const toast = useToast();
  const refresh = useRefreshTreasury();
  const accounts = useBankAccounts(companyId);
  const [bankAccountId, setBankAccountId] = useState("");
  const [name, setName] = useState("");
  const [referenceNo, setReferenceNo] = useState("");
  const [principal, setPrincipal] = useState<string | null>(null);
  const [annualRate, setAnnualRate] = useState("");
  const [startDate, setStartDate] = useState<string | null>(todayIstanbul());
  const [note, setNote] = useState("");
  const [recordDisbursement, setRecordDisbursement] = useState(true);
  const [rows, setRows] = useState<InstallmentState[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(): Promise<void> {
    if (!bankAccountId) return setError(t("treasury.loans.pickAccount"));
    if (!startDate) return setError(t("treasury.loans.pickStartDate"));
    setBusy(true);
    setError(null);
    try {
      await api.POST("/loans", {
        params: { header: { "Idempotency-Key": newIdempotencyKey() } },
        body: {
          companyId,
          bankAccountId,
          name: name.trim(),
          referenceNo: referenceNo.trim() || null,
          principal: minorOf(principal).toString(),
          annualRate: annualRate.trim().replace(",", ".") || null,
          startDate,
          note: note.trim() || null,
          recordDisbursement,
          installments: toInstallmentPayload(rows),
        },
      });
      await refresh(companyId);
      toast({ tone: "success", title: t("treasury.loans.saved") });
      setName("");
      setPrincipal(null);
      setRows([]);
      onClose();
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => !next && !busy && onClose()}
      title={t("treasury.loans.create")}
      description={t("treasury.loans.dialogDescription")}
      className="sm:max-w-4xl"
      dismissible={!busy}
    >
      <form
        className="grid gap-4"
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          void submit();
        }}
      >
        {error ? <Alert tone="danger">{error}</Alert> : null}
        <div className="grid gap-4 sm:grid-cols-3">
          <FormField label={t("treasury.fields.name")} required>
            {(control) => (
              <Input
                {...control}
                value={name}
                autoFocus
                maxLength={120}
                onChange={(event) => setName(event.target.value)}
              />
            )}
          </FormField>
          <FormField label={t("treasury.fields.bankAccount")} required>
            {(control) => (
              <Select
                {...control}
                value={bankAccountId}
                onChange={(event) => setBankAccountId(event.target.value)}
              >
                <option value="">{t("common.select")}</option>
                {(accounts.data ?? []).map((account) => (
                  <option key={account.id} value={account.id}>
                    {account.bankName} · {account.name}
                  </option>
                ))}
              </Select>
            )}
          </FormField>
          <FormField label={t("treasury.loans.referenceNo")}>
            {(control) => (
              <Input
                {...control}
                value={referenceNo}
                className="font-mono"
                maxLength={60}
                onChange={(event) => setReferenceNo(event.target.value)}
              />
            )}
          </FormField>
          <FormField label={t("treasury.loans.principal")} required>
            {(control) => <MoneyInput {...control} value={principal} onValueChange={setPrincipal} />}
          </FormField>
          <FormField label={t("treasury.loans.annualRate")}>
            {(control) => (
              <Input
                {...control}
                value={annualRate}
                inputMode="decimal"
                onChange={(event) => setAnnualRate(event.target.value)}
              />
            )}
          </FormField>
          <FormField label={t("treasury.loans.startDateLabel")} required>
            {(control) => <DateInput {...control} value={startDate} onValueChange={setStartDate} />}
          </FormField>
        </div>
        <FormField label={t("treasury.dbs.note")}>
          {(control) => (
            <Textarea
              {...control}
              rows={2}
              maxLength={500}
              value={note}
              onChange={(event) => setNote(event.target.value)}
            />
          )}
        </FormField>
        <label className="flex items-center gap-2 text-sm">
          <Checkbox
            checked={recordDisbursement}
            onChange={(event) => setRecordDisbursement(event.target.checked)}
          />
          {t("treasury.loans.recordDisbursement")}
        </label>

        <fieldset className="grid gap-3 rounded-md border p-4">
          <legend className="px-1 text-sm font-medium">{t("treasury.loans.planTitle")}</legend>
          <p className="text-sm text-muted-foreground">{t("treasury.loans.planHint")}</p>
          <InstallmentEditor rows={rows} onChange={setRows} />
        </fieldset>

        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose} disabled={busy}>
            {t("common.cancel")}
          </Button>
          <Button type="submit" loading={busy}>
            {t("common.save")}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

/** Ödenmemiş taksit planını değiştirme: ödenmiş taksitler korunur, kalanı bu listeyle değiştirilir. */
function PlanDialog({
  companyId,
  loan,
  open,
  onClose,
}: {
  companyId: string;
  loan: Loan;
  open: boolean;
  onClose: () => void;
}): ReactElement {
  const { t } = useTranslation();
  const toast = useToast();
  const refresh = useRefreshTreasury();
  const [rows, setRows] = useState<InstallmentState[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [loaded, setLoaded] = useState(false);
  if (open && !loaded) {
    setLoaded(true);
    setRows(
      loan.installments
        .filter((installment) => installment.status === "PLANNED")
        .map((installment) => ({
          id: installment.id,
          installmentNo: installment.installmentNo,
          dueDate: installment.dueDate,
          principalAmount: installment.principalAmount,
          interestAmount: installment.interestAmount,
          otherAmount: installment.otherAmount,
        })),
    );
  }
  if (!open && loaded) setLoaded(false);

  async function submit(): Promise<void> {
    if (rows.length === 0) return setError(t("treasury.loans.planEmpty"));
    setBusy(true);
    setError(null);
    try {
      await api.PUT("/loans/{id}/installments", {
        params: { path: { id: loan.id } },
        body: { installments: toInstallmentPayload(rows), version: loan.version },
      });
      await refresh(companyId);
      toast({ tone: "success", title: t("treasury.loans.planSaved") });
      onClose();
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => !next && !busy && onClose()}
      title={t("treasury.loans.editPlan")}
      description={t("treasury.loans.editPlanDescription")}
      className="sm:max-w-4xl"
      dismissible={!busy}
    >
      <form
        className="grid gap-4"
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          void submit();
        }}
      >
        {error ? <Alert tone="danger">{error}</Alert> : null}
        <InstallmentEditor rows={rows} onChange={setRows} />
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose} disabled={busy}>
            {t("common.cancel")}
          </Button>
          <Button type="submit" loading={busy}>
            {t("common.save")}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
